import React, { useEffect, useState, useRef } from 'react';
/**
 * Smoothly tweens a numeric value to a target over `duration` ms using an
 * easeOutQuad curve. Rounds to integer for display so the rendered text stays
 * crisp on a tabular-nums font.
 */
export function AnimatedNumber({
  value,
  duration = 350,
  className
}: {
  value: number;
  duration?: number;
  className?: string;
}) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef<number | null>(null);
  const fromRef = useRef(value);
  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 2); // easeOutQuad
      const current = from + (to - from) * eased;
      fromRef.current = current;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value, duration]);
  return <span className={className} data-id="element-296">{Math.round(display)}</span>;
}